import { useCallback, useEffect, useMemo, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { Box, Stack } from '@mui/material';
import { addHours, format, roundToNearestMinutes } from 'date-fns';
import { CalendarDays, Check, X } from 'lucide-react';
import { useFormContext } from 'react-hook-form';

import { gql } from '@graphql/__generated__';
import {
  type Activities_Insert_Input,
  type Activities_Set_Input,
} from '@graphql/__generated__/graphql';
import { getFullName } from '@modules/_shared/components/email-form/contextSuggestions';
import {
  AdvancedFieldsToggle,
  type LeadActivityAdvancedFormValues,
  buildLeadActivityAdvancedFields,
} from '@modules/_shared/components/feed/ActivityAdvancedFieldsToggle';
import { ActivityDialogShell } from '@modules/_shared/components/feed/ActivityDialogShell';
import {
  StatusButtons,
  type StatusOption,
} from '@modules/_shared/components/feed/ActivityStatusButtons';
import type {
  ActivityType,
  ParentTypeOption,
} from '@modules/_shared/components/feed/types';
import {
  type ActivityDetails,
  mapCallActivityToFormValues,
  mapNoteActivityToFormValues,
  mapTaskActivityToFormValues,
  mapVisitActivityToFormValues,
} from '@modules/_shared/components/feed/utils/activityEditMappers';
import {
  type PreviousActivityState,
  buildTargetUserObjects,
  createActivityMappers,
  mapNoteFormValuesToInsert,
  mapNoteFormValuesToUpdate,
} from '@modules/_shared/components/feed/utils/activityMutationUtils';
import {
  GET_PENDING_ACTIVITIES,
  INSERT_ACTIVITY,
  UPDATE_ACTIVITY,
} from '@modules/_shared/components/feed/utils/activityQueries';
import { RaDate } from '@modules/_shared/components/form/RaDate';
import {
  type FormDefinitionType,
  type RaFormOnChange,
} from '@modules/_shared/components/form/RaForm';
import { RaNumber } from '@modules/_shared/components/form/RaNumber';
import { RaTime } from '@modules/_shared/components/form/RaTime';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { getDefaultSubject } from '@modules/_shared/utils/activity';
import { getContactDisplayNames } from '@modules/_shared/utils/contact';
import { emitFeedRefresh } from '@modules/_shared/utils/feed-refresh';
import { formatAddress } from '@modules/_shared/utils/formatting';
import { isNullOrEmpty } from '@modules/_shared/utils/strings';
import { useLeadActivitySubjectAutoUpdate } from '@modules/lead-details/hooks/useLeadActivitySubjectAutoUpdate';
import type { LeadDetailsDrawer as LeadDetailsDrawerData } from '@modules/lead-details/type';
import { useLocale } from '@shared/hooks/locale';

const GET_ACTIVITY_FOR_EDIT = gql(/* GraphQL */ `
  query LeadDetailsActivityForEdit($id: uuid!) {
    activities_by_pk(id: $id) {
      id
      activity_type
      subject
      note
      done
      success
      due_at
      start_date
      end_at
      location
      assigned_to
      created_by
      created_at
      lead_id
      lot_id
      buyer_lead_id
      user_id
      visibility
      activities_target_users {
        target_user_id
      }
    }
  }
`);

const GET_USER_DETAILS = gql(/* GraphQL */ `
  query GetUserDetailsForActivity($user_id: uuid!) {
    users_by_pk(id: $user_id) {
      id
      first_name
      last_name
    }
  }
`);

const GET_BUYER_LEAD_DETAILS = gql(/* GraphQL */ `
  query GetBuyerLeadDetailsForActivity($buyer_lead_id: uuid!) {
    buyer_leads_by_pk(id: $buyer_lead_id) {
      id
      lot_id
      user {
        id
        first_name
        last_name
      }
    }
  }
`);

const GET_LOT_DETAILS = gql(/* GraphQL */ `
  query GetLotDetailsForActivity($lot_id: uuid!) {
    lots_by_pk(id: $lot_id) {
      id
      property {
        google_address {
          formatted_address
        }
        street_number
        route
        postcode
        locality
        state
        country_code
      }
      sellers {
        lot_id
        user_id
        seller {
          id
          first_name
          last_name
        }
      }
    }
  }
`);

// Types

type CallStatus = 'planned' | 'successful' | 'missed';
type VisitStatus = 'planned' | 'successful' | 'cancelled';
type TaskStatus = 'planned' | 'completed';

type BaseActivityFormValues = LeadActivityAdvancedFormValues & {
  subject?: string;
  notes?: string;
  date?: string | null;
  time?: string;
};

type CallFormValues = BaseActivityFormValues & {
  subject: string;
  status: CallStatus;
};

type VisitFormValues = BaseActivityFormValues & {
  subject: string;
  status: VisitStatus;
  duration: number | null;
  location: string;
};

type TaskFormValues = BaseActivityFormValues & {
  subject: string;
  status: TaskStatus;
};

type NoteFormValues = LeadActivityAdvancedFormValues & {
  note: string;
};

type UnifiedActivityFormValues =
  | CallFormValues
  | VisitFormValues
  | TaskFormValues
  | NoteFormValues;

type ParentContext = {
  type: ParentTypeOption;
  id: string | null;
};

type UnifiedActivityDialogProps = {
  open: boolean;
  onClose: () => void;
  activityType: ActivityType | undefined;
  mode?: 'create' | 'edit';
  activityId?: string | null;
  // Parent context - can be lead or non-lead
  parent?: ParentContext;
  // Optional: if parent is a lead and you have the full data
  leadData?: LeadDetailsDrawerData | null;
  initialContactIds?: string[] | null;
  contextLotId?: string | null;
  onCreate?: (values: UnifiedActivityFormValues) => void | Promise<void>;
};

// Status button configurations
const callStatusOptions: Array<StatusOption<CallStatus>> = [
  {
    value: 'planned',
    icon: CalendarDays,
    getLabel: t => t('Plan call'),
  },
  {
    value: 'successful',
    icon: Check,
    getLabel: t => t('Log successful call'),
  },
  {
    value: 'missed',
    icon: X,
    getLabel: t => t('Log missed call'),
  },
];

const visitStatusOptions: Array<StatusOption<VisitStatus>> = [
  {
    value: 'planned',
    icon: CalendarDays,
    getLabel: t => t('Plan visit'),
  },
  {
    value: 'successful',
    icon: Check,
    getLabel: t => t('Log successful visit'),
  },
  {
    value: 'cancelled',
    icon: X,
    getLabel: t => t('Log cancelled visit'),
  },
];

const taskStatusOptions: Array<StatusOption<TaskStatus>> = [
  {
    value: 'planned',
    icon: CalendarDays,
    getLabel: t => t('Plan task'),
  },
  {
    value: 'completed',
    icon: Check,
    getLabel: t => t('Log completed task'),
  },
];

const CallStatusField = () => (
  <StatusButtons<CallStatus> name="status" options={callStatusOptions} />
);

const VisitStatusField = () => (
  <StatusButtons<VisitStatus> name="status" options={visitStatusOptions} />
);

const TaskStatusField = () => (
  <StatusButtons<TaskStatus> name="status" options={taskStatusOptions} />
);

const CallScheduleField = () => {
  const { t } = useLocale();
  const { control } = useFormContext<CallFormValues>();

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
      <Box sx={{ width: { xs: '100%', sm: 200 } }}>
        <RaDate
          enableShortcuts
          control={control}
          name="date"
          label={t('Date')}
          required
        />
      </Box>
      <Box sx={{ width: { xs: '100%', sm: 140 } }}>
        <RaTime control={control} name="time" label={t('Time')} />
      </Box>
    </Stack>
  );
};

const VisitScheduleField = () => {
  const { t } = useLocale();
  const { control, watch, setValue } = useFormContext<VisitFormValues>();
  const time = watch('time');
  const hasTime = time != null && time.trim() !== '';

  useEffect(() => {
    if (!hasTime) {
      setValue('duration', null, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [hasTime, setValue]);

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
      <Box sx={{ width: { xs: '100%', sm: 200 } }}>
        <RaDate
          enableShortcuts
          control={control}
          name="date"
          label={t('Date')}
          required
        />
      </Box>
      <Box sx={{ width: { xs: '100%', sm: 140 } }}>
        <RaTime control={control} name="time" label={t('Time')} />
      </Box>
      <Box sx={{ width: { xs: '100%', sm: 140 } }}>
        <RaNumber
          control={control}
          name="duration"
          label={t('Duration (min)')}
          required={hasTime}
          disabled={!hasTime}
        />
      </Box>
    </Stack>
  );
};

const TaskScheduleField = () => {
  const { t } = useLocale();
  const { control } = useFormContext<TaskFormValues>();

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
      <Box sx={{ width: { xs: '100%', sm: 200 } }}>
        <RaDate
          enableShortcuts
          control={control}
          name="date"
          label={t('Date')}
          required
        />
      </Box>
      <Box sx={{ width: { xs: '100%', sm: 140 } }}>
        <RaTime control={control} name="time" label={t('Time')} />
      </Box>
    </Stack>
  );
};

const callMappers = createActivityMappers('call');
const visitMappers = createActivityMappers('visit');
const taskMappers = createActivityMappers('task');

const DEFAULT_SCHEDULE_HOURS_AHEAD = 2;
const DEFAULT_SCHEDULE_MINUTES_ROUNDING = 15;

export const UnifiedActivityDialog = ({
  open,
  onClose,
  activityType,
  mode = 'create',
  activityId,
  parent,
  leadData,
  initialContactIds,
  contextLotId,
  onCreate,
}: UnifiedActivityDialogProps) => {
  const { t } = useLocale();
  const { me } = useAppData();
  const [expanded, setExpanded] = useState(false);
  const isAdmin = me?.is_admin === true;

  const [errorLike, setErrorLike] = useState<ErrorLike | null>(null);

  // Fetch User data if parent is User
  const shouldFetchUser = parent?.type === 'User' && parent?.id && open;
  const { data: fetchedUserData, error: userError } = useQuery(
    GET_USER_DETAILS,
    {
      variables: { user_id: parent?.id ?? '' },
      skip: !shouldFetchUser,
    },
  );

  // Fetch BuyerLead data if parent is BuyerLead
  const shouldFetchBuyerLead =
    parent?.type === 'BuyerLead' && parent?.id && open;
  const { data: fetchedBuyerLeadData, error: buyerLeadError } = useQuery(
    GET_BUYER_LEAD_DETAILS,
    {
      variables: { buyer_lead_id: parent?.id ?? '' },
      skip: !shouldFetchBuyerLead,
    },
  );

  // Seeds the Location default of a visit planned from a buyer lead, where no
  // caller passes a `contextLotId`. Only in create mode: editing builds the form
  // from the saved activity and never reads the lot, so fetching it there would
  // just add a round trip to the loading gate. The lot row stays behind the
  // agent RLS chain (own tenant + lots_access_view), so `GET_LOT_DETAILS`
  // resolves to null for an MLS listing shared by another tenant or a listing
  // this agent cannot access — Location then stays empty rather than leaking it.
  const buyerLeadLotId =
    mode === 'create'
      ? fetchedBuyerLeadData?.buyer_leads_by_pk?.lot_id ?? null
      : null;

  const lotIdForContext =
    parent?.type === 'Lot'
      ? parent?.id ?? null
      : activityType === 'visit'
      ? contextLotId ?? buyerLeadLotId
      : null;
  const shouldFetchLot = lotIdForContext != null && open;
  const { data: fetchedLotData, error: lotError } = useQuery(GET_LOT_DETAILS, {
    variables: { lot_id: lotIdForContext ?? '' },
    skip: !shouldFetchLot,
  });

  // Compute default contact ID based on parent type
  const defaultContactId = useMemo(() => {
    if (parent?.type === 'Lead' && leadData?.contact?.id) {
      return leadData.contact.id;
    }
    if (parent?.type === 'User' && fetchedUserData?.users_by_pk?.id) {
      return fetchedUserData.users_by_pk.id;
    }
    if (
      parent?.type === 'BuyerLead' &&
      fetchedBuyerLeadData?.buyer_leads_by_pk?.user?.id
    ) {
      return fetchedBuyerLeadData.buyer_leads_by_pk.user.id;
    }
    if (
      parent?.type === 'Lot' &&
      fetchedLotData?.lots_by_pk?.sellers?.[0]?.seller?.id
    ) {
      return fetchedLotData.lots_by_pk.sellers[0].seller.id;
    }
    return null;
  }, [
    parent?.type,
    leadData,
    fetchedUserData,
    fetchedBuyerLeadData,
    fetchedLotData,
  ]);

  const shouldFetchActivity = mode === 'edit' && Boolean(activityId);
  const {
    data: activityData,
    loading: activityLoading,
    error: activityError,
  } = useQuery(GET_ACTIVITY_FOR_EDIT, {
    variables: { id: activityId ?? '' },
    skip: !shouldFetchActivity,
    fetchPolicy: 'network-only',
  });

  // Apollo v4 removed `onError` from `useQuery`; surface query errors via the
  // same snackbar that mutation onError callbacks feed.
  useEffect(() => {
    const queryError = userError ?? buyerLeadError ?? lotError ?? activityError;
    if (queryError) {
      setErrorLike(queryError);
    }
  }, [userError, buyerLeadError, lotError, activityError]);

  const activityDetails = useMemo(() => {
    return (
      (activityData?.activities_by_pk as ActivityDetails | undefined) ?? null
    );
  }, [activityData]);

  const pendingActivitiesRefetchQueries = useMemo(
    () =>
      // Refetch by document so Apollo re-runs the active query with its own
      // `where` variables (set in ActivityProvider). Passing explicit
      // `{ lead_id, user_id }` variables here sent the query without its
      // required non-nullable `$where`, which Hasura rejected with
      // "expecting a value for non-nullable variable: where".
      parent?.id != null && me?.id != null ? [GET_PENDING_ACTIVITIES] : [],
    [me?.id, parent?.id],
  );

  const [insertActivity] = useMutation(INSERT_ACTIVITY, {
    onError: setErrorLike,
  });

  const [updateActivity] = useMutation(UPDATE_ACTIVITY, {
    onError: setErrorLike,
  });

  const getParticipantsForParent = useCallback(() => {
    const result = [];

    const isExplicitContact = (id?: string | null) =>
      initialContactIds == null
        ? true
        : id != null && initialContactIds.includes(id);

    if (parent?.type === 'Lead' && leadData?.contact) {
      if (!isExplicitContact(leadData.contact.id)) {
        return [];
      }
      const { displayName } = getContactDisplayNames(leadData, true);
      return displayName ? [{ name: displayName }] : [];
    }

    if (parent?.type === 'User' && fetchedUserData?.users_by_pk) {
      const user = fetchedUserData.users_by_pk;
      if (isExplicitContact(user.id)) {
        result.push({ name: getFullName(user) });
      }
    }

    if (
      parent?.type === 'BuyerLead' &&
      fetchedBuyerLeadData?.buyer_leads_by_pk?.user
    ) {
      const user = fetchedBuyerLeadData.buyer_leads_by_pk.user;
      if (isExplicitContact(user.id)) {
        result.push({ name: getFullName(user) });
      }
    }

    if (parent?.type === 'Lot' && fetchedLotData?.lots_by_pk?.sellers) {
      fetchedLotData.lots_by_pk.sellers.forEach(sellerEntry => {
        if (sellerEntry?.seller && isExplicitContact(sellerEntry.seller.id)) {
          result.push({ name: getFullName(sellerEntry.seller) });
        }
      });
    }

    return result;
  }, [
    parent?.type,
    leadData,
    fetchedUserData,
    fetchedBuyerLeadData,
    fetchedLotData,
    initialContactIds,
  ]);

  // Shared base form fields for all activities
  const getBaseFormFields = useCallback(() => {
    return {
      assigned_to: me?.id ?? null,
      contact_ids:
        initialContactIds ?? (defaultContactId ? [defaultContactId] : []),
      created_by: me?.id ?? null,
      parent_type: parent?.type ?? 'Lead',
      parent_id: parent?.id ?? null,
      visibility: 'shared' as const,
    };
  }, [me, defaultContactId, parent, initialContactIds]);

  // Shared computed values for scheduled activities
  const getScheduledActivityDefaults = useCallback(() => {
    const inTwoHours = roundToNearestMinutes(
      addHours(new Date(), DEFAULT_SCHEDULE_HOURS_AHEAD),
      { nearestTo: DEFAULT_SCHEDULE_MINUTES_ROUNDING },
    );

    const participants = getParticipantsForParent();

    const assignedTo = {
      firstName: me?.first_name,
      lastName: me?.last_name,
      teamName: me?.default_team?.name,
    };

    return {
      inTwoHours,
      participants,
      assignedTo,
    };
  }, [me, getParticipantsForParent]);

  // Compute defaults for each activity type
  const buildCallDefaults = useCallback((): CallFormValues => {
    const { inTwoHours, participants, assignedTo } =
      getScheduledActivityDefaults();
    const baseFields = getBaseFormFields();

    const defaultSubject = getDefaultSubject({
      t,
      activityType: 'call',
      assignedTo,
      participants,
    });

    return {
      ...baseFields,
      subject: defaultSubject,
      status: 'planned',
      date: format(inTwoHours, 'yyyy-MM-dd'),
      time: '',
      notes: '',
    };
  }, [getScheduledActivityDefaults, getBaseFormFields, t]);

  const buildVisitDefaults = useCallback((): VisitFormValues => {
    const { inTwoHours, participants, assignedTo } =
      getScheduledActivityDefaults();
    const baseFields = getBaseFormFields();

    const defaultSubject = getDefaultSubject({
      t,
      activityType: 'visit',
      assignedTo,
      participants,
    });

    const property = leadData?.property ?? fetchedLotData?.lots_by_pk?.property;

    const getPropertyLocation = (prop: typeof property) => {
      if (!prop) {
        return '';
      }

      const formattedAddress = prop.google_address?.formatted_address ?? null;
      if (!isNullOrEmpty(formattedAddress, true)) {
        return formattedAddress ?? '';
      }

      return formatAddress(prop, ', ', false) ?? '';
    };

    const propertyLocation = getPropertyLocation(property);

    return {
      ...baseFields,
      subject: defaultSubject,
      status: 'planned',
      date: format(inTwoHours, 'yyyy-MM-dd'),
      time: format(inTwoHours, 'HH:mm'),
      duration: 60,
      location: propertyLocation,
      notes: '',
    };
  }, [
    getScheduledActivityDefaults,
    getBaseFormFields,
    t,
    leadData,
    fetchedLotData,
  ]);

  const buildTaskDefaults = useCallback((): TaskFormValues => {
    const { inTwoHours } = getScheduledActivityDefaults();
    const baseFields = getBaseFormFields();

    return {
      ...baseFields,
      subject: '',
      status: 'planned',
      date: format(inTwoHours, 'yyyy-MM-dd'),
      time: '',
      notes: '',
    };
  }, [getScheduledActivityDefaults, getBaseFormFields]);

  const buildNoteDefaults = useCallback((): NoteFormValues => {
    const baseFields = getBaseFormFields();

    return {
      ...baseFields,
      note: '',
    };
  }, [getBaseFormFields]);

  const buildCreateDefaults = useCallback(() => {
    switch (activityType) {
      case 'call':
        return buildCallDefaults();
      case 'visit':
        return buildVisitDefaults();
      case 'task':
        return buildTaskDefaults();
      case 'note':
        return buildNoteDefaults();
    }
  }, [
    activityType,
    buildCallDefaults,
    buildVisitDefaults,
    buildTaskDefaults,
    buildNoteDefaults,
  ]);

  const buildDefaultValues = useCallback(() => {
    if (shouldFetchActivity && activityDetails) {
      switch (activityType) {
        case 'call':
          return mapCallActivityToFormValues(
            activityDetails,
            leadData ?? null,
            me?.id ?? null,
          );
        case 'visit':
          return mapVisitActivityToFormValues(
            activityDetails,
            leadData ?? null,
            me?.id ?? null,
          );
        case 'task':
          return mapTaskActivityToFormValues(
            activityDetails,
            leadData ?? null,
            me?.id ?? null,
          );
        case 'note':
          return mapNoteActivityToFormValues(
            activityDetails,
            leadData ?? null,
            me?.id ?? null,
          );
      }
    }

    return buildCreateDefaults();
  }, [
    shouldFetchActivity,
    activityDetails,
    leadData,
    activityType,
    me?.id,
    buildCreateDefaults,
  ]);

  const defaultValues = useMemo(() => {
    if (!open) {
      return {};
    }

    return buildDefaultValues() as UnifiedActivityFormValues;
  }, [open, buildDefaultValues]);

  // Contact details for advanced fields
  const contactDetails = useMemo(() => {
    if (!leadData?.contact) {
      return null;
    }
    const email = leadData.contact.emails?.[0]?.email ?? null;
    const phone = leadData.contact.phone_numbers?.[0]?.number ?? null;
    return [phone, email]
      .filter(value => value && value.trim() !== '')
      .join(' • ');
  }, [leadData]);

  const defaultSubjectForHook =
    activityType !== 'note'
      ? (defaultValues as CallFormValues | VisitFormValues | TaskFormValues)
          .subject ?? ''
      : '';

  const hookResult = useLeadActivitySubjectAutoUpdate({
    activityType:
      activityType !== 'note'
        ? (activityType as 'call' | 'visit' | 'task')
        : 'call',
    defaultSubject: defaultSubjectForHook,
  });

  const handleFormChangeForScheduled =
    activityType !== 'note' && leadData != null ? hookResult : undefined;

  // Advanced fields
  const getAdvancedFields = useMemo(() => {
    return () =>
      buildLeadActivityAdvancedFields<UnifiedActivityFormValues>({
        expanded,
        t,
        contactDetails,
        showCreatedBy: isAdmin,
        showParentFields: isAdmin,
        showAssigneeField: activityType !== 'note',
        showVisibilityField: isAdmin,
        createdAt:
          mode !== 'edit' || !activityDetails?.created_at
            ? null
            : activityDetails.created_at,
      });
  }, [
    activityDetails?.created_at,
    activityType,
    contactDetails,
    expanded,
    isAdmin,
    mode,
    t,
  ]);

  // Form definition based on activity type
  const formDefinition = useMemo<
    FormDefinitionType<UnifiedActivityFormValues>
  >(() => {
    if (activityType === 'note') {
      return ({ t }) => {
        const advancedFields = getAdvancedFields();

        return [
          {
            type: 'rich-text',
            name: 'note',
            label: t('Note'),
            required: true,
            gridProps: { md: 12 },
            minHeight: '130px',
          },
          {
            type: 'custom' as const,
            name: '__toggle_more',
            element: (
              <AdvancedFieldsToggle
                expanded={expanded}
                onToggle={setExpanded}
              />
            ),
            gridProps: { md: 12 },
          },
          ...advancedFields,
        ];
      };
    }

    if (activityType === 'call') {
      return ({ t }) => [
        {
          type: 'text',
          name: 'subject',
          label: t('Subject'),
          required: true,
          gridProps: { md: 12 },
        },
        {
          type: 'custom',
          name: 'statusField',
          element: <CallStatusField />,
          gridProps: { md: 12 },
        },
        {
          type: 'custom',
          name: 'schedule',
          element: <CallScheduleField />,
          gridProps: { md: 12 },
        },
        {
          type: 'rich-text',
          name: 'notes',
          label: t('Notes'),
          gridProps: { md: 12 },
          minHeight: '130px',
        },
        {
          type: 'custom',
          name: '__advanced_toggle',
          element: (
            <AdvancedFieldsToggle expanded={expanded} onToggle={setExpanded} />
          ),
          gridProps: { md: 12 },
        },
        ...getAdvancedFields(),
      ];
    }

    if (activityType === 'visit') {
      return ({ t }) => [
        {
          type: 'text',
          name: 'subject',
          label: t('Subject'),
          required: true,
          gridProps: { md: 12 },
        },
        {
          type: 'custom',
          name: 'statusField',
          element: <VisitStatusField />,
          gridProps: { md: 12 },
        },
        {
          type: 'custom',
          name: 'schedule',
          element: <VisitScheduleField />,
          gridProps: { md: 12 },
        },
        {
          type: 'text',
          name: 'location',
          label: t('Location'),
          gridProps: { md: 12 },
        },
        {
          type: 'rich-text',
          name: 'notes',
          label: t('Notes'),
          gridProps: { md: 12 },
          minHeight: '130px',
        },
        {
          type: 'custom' as const,
          name: '__toggle_more',
          element: (
            <AdvancedFieldsToggle expanded={expanded} onToggle={setExpanded} />
          ),
          gridProps: { md: 12 },
        },
        ...getAdvancedFields(),
      ];
    }

    // task
    return ({ t }) => [
      {
        type: 'text',
        name: 'subject',
        label: t('Subject'),
        required: true,
        gridProps: { md: 12 },
      },
      {
        type: 'custom',
        name: 'statusField',
        element: <TaskStatusField />,
        gridProps: { md: 12 },
      },
      {
        type: 'custom',
        name: 'schedule',
        element: <TaskScheduleField />,
        gridProps: { md: 12 },
      },
      {
        type: 'rich-text',
        name: 'notes',
        label: t('Notes'),
        gridProps: { md: 12 },
        minHeight: '130px',
      },
      {
        type: 'custom' as const,
        name: '__toggle_more',
        element: (
          <AdvancedFieldsToggle expanded={expanded} onToggle={setExpanded} />
        ),
        gridProps: { md: 12 },
      },
      ...getAdvancedFields(),
    ];
  }, [activityType, expanded, getAdvancedFields]);

  const handleSubmit = async (values: UnifiedActivityFormValues) => {
    if (mode === 'edit' && activityId) {
      let setInput: Activities_Set_Input;
      const wasDone = activityDetails?.done === true;
      const previousSchedule = defaultValues as Partial<BaseActivityFormValues>;
      const previous: PreviousActivityState = {
        done: wasDone,
        date: previousSchedule.date,
        time: previousSchedule.time,
      };
      if (activityType === 'note') {
        setInput = mapNoteFormValuesToUpdate(
          values as NoteFormValues,
          isAdmin,
          wasDone,
        );
      } else if (activityType === 'call') {
        setInput = callMappers.toUpdate(
          values as CallFormValues,
          isAdmin,
          previous,
        );
      } else if (activityType === 'visit') {
        setInput = visitMappers.toUpdate(
          values as VisitFormValues,
          isAdmin,
          previous,
        );
      } else {
        setInput = taskMappers.toUpdate(
          values as TaskFormValues,
          isAdmin,
          previous,
        );
      }

      const targetUsers = buildTargetUserObjects(activityId, values);

      // Check if target users have changed
      const existingTargetUserIds =
        activityDetails?.activities_target_users
          ?.map(tu => tu.target_user_id)
          .sort() ?? [];
      const newTargetUserIds = targetUsers.map(tu => tu.target_user_id).sort();

      const updateTargetUsers =
        existingTargetUserIds.length !== newTargetUserIds.length ||
        !existingTargetUserIds.every(
          (id, index) => id === newTargetUserIds[index],
        );

      await updateActivity({
        variables: {
          id: activityId,
          set: setInput,
          targetUsers,
          updateTargetUsers,
        },
        refetchQueries: pendingActivitiesRefetchQueries,
        awaitRefetchQueries: pendingActivitiesRefetchQueries.length > 0,
      });
    } else {
      // Create new activity
      let object: Activities_Insert_Input;
      const currentUserId = me?.id ?? null;
      if (activityType === 'note') {
        object = mapNoteFormValuesToInsert(
          values as NoteFormValues,
          currentUserId,
          isAdmin,
        );
      } else if (activityType === 'call') {
        object = callMappers.toInsert(
          values as CallFormValues,
          currentUserId,
          isAdmin,
        );
      } else if (activityType === 'visit') {
        object = visitMappers.toInsert(
          values as VisitFormValues,
          currentUserId,
          isAdmin,
        );
      } else {
        object = taskMappers.toInsert(
          values as TaskFormValues,
          currentUserId,
          isAdmin,
        );
      }

      await insertActivity({
        variables: { object },
        refetchQueries: pendingActivitiesRefetchQueries,
        awaitRefetchQueries: pendingActivitiesRefetchQueries.length > 0,
      });
    }

    emitFeedRefresh();

    await onCreate?.(values);
  };

  const handleClose = () => {
    setExpanded(false);
    onClose();
  };

  const dialogTitle = {
    call: t('Call'),
    visit: t('Visit'),
    task: t('Task'),
    note: t('Write a note'),
  }[activityType as string];

  const isContextLotPending =
    shouldFetchLot && fetchedLotData == null && lotError == null;

  const isContextBuyerLeadPending =
    Boolean(shouldFetchBuyerLead) &&
    fetchedBuyerLeadData == null &&
    buyerLeadError == null;

  const isContextUserPending =
    Boolean(shouldFetchUser) && fetchedUserData == null && userError == null;

  const isLoadingData =
    (shouldFetchActivity && (activityLoading || !activityDetails)) ||
    isContextLotPending ||
    isContextBuyerLeadPending ||
    isContextUserPending;

  return (
    <>
      <ActivityDialogShell<UnifiedActivityFormValues>
        open={open}
        onClose={handleClose}
        title={dialogTitle ?? ''}
        defaultValues={defaultValues}
        formDefinition={formDefinition}
        onCreate={handleSubmit}
        onChange={
          handleFormChangeForScheduled as
            | RaFormOnChange<UnifiedActivityFormValues>
            | undefined
        }
        mode={mode}
        isLoadingData={isLoadingData}
      />
      <MutationErrorSnackbar
        error={errorLike}
        onClose={() => setErrorLike(null)}
      />
    </>
  );
};
